import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  Verified,
  VerifiedOutlined,
  CheckCircle,
  Cancel,
  Search,
  FilterList,
  Clear,
} from '@mui/icons-material';

const AcademicQuestionPaperList = ({
  questionPapers,
  totalElements,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
  onVerify,
  onToggleActive,
  onSearch,
  loading = false,
}) => {
  const [searchParams, setSearchParams] = useState({
    subject: '',
    standard: '',
    academicYear: '',
    isVerified: false,
    isActive: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const handleSearchChange = (field, value) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearchSubmit = () => {
    const params = {};
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] !== '' && searchParams[key] !== null && searchParams[key] !== undefined) {
        params[key] = searchParams[key];
      }
    });
    onSearch(params);
  };

  const handleClearFilters = () => {
    setSearchParams({
      subject: '',
      standard: '',
      academicYear: '',
      isVerified: '',
      isActive: '',
    });
    onSearch({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Search />
            <Typography variant="h6">Search Question Papers</Typography>
          </Box>
          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
            size="small"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={searchParams.subject}
              onChange={(e) => handleSearchChange('subject', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Standard"
              value={searchParams.standard}
              onChange={(e) => handleSearchChange('standard', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Academic Year"
              value={searchParams.academicYear}
              onChange={(e) => handleSearchChange('academicYear', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Verified</InputLabel>
              <Select
                value={searchParams.isVerified}
                onChange={(e) => handleSearchChange('isVerified', e.target.value)}
                label="Verified"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Verified</MenuItem>
                <MenuItem value="false">Not Verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {showFilters && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={searchParams.isActive}
                    onChange={(e) => handleSearchChange('isActive', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                startIcon={<Search />}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Standard</TableCell>
                <TableCell>Max Marks</TableCell>
                <TableCell>Academic Year</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : questionPapers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No question papers found
                  </TableCell>
                </TableRow>
              ) : (
                questionPapers.map((paper, index) => (
                  <TableRow key={paper.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Chip label={paper.subject} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{paper.standard}</TableCell>
                    <TableCell>{paper.maximumMarks || '-'}</TableCell>
                    <TableCell>{paper.academicYear}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${paper.questionPapers?.length || 0} questions`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          icon={paper.isVerified ? <Verified /> : <VerifiedOutlined />}
                          label={paper.isVerified ? 'Verified' : 'Not Verified'}
                          size="small"
                          color={paper.isVerified ? 'success' : 'default'}
                          variant={paper.isVerified ? 'filled' : 'outlined'}
                        />
                        <Chip
                          label={paper.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={paper.isActive ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(paper.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => onView(paper.id)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(paper.id)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete(paper.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                      {!paper.isVerified && (
                        <Tooltip title="Verify">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => onVerify(paper.id)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={paper.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          color={paper.isActive ? 'warning' : 'success'}
                          onClick={() => onToggleActive(paper.id)}
                        >
                          {paper.isActive ? <Cancel /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalElements || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
};

export default AcademicQuestionPaperList;